// RCPT2 (docs/build-briefs/RCPT2-emailed-receipts-and-paperless.md; PRD P0.8; DEC-403, DEC-016). The Receipt row under a closed tab
// in Open Tabs' Recently closed (the only place a closed ticket is listed):
// where an emailed receipt went and whether the provider took it, and, for a session holding money.receipt.resend, a Resend to a
// corrected address. Drawn only for a session that may view the evidence (money.signature.view) and only when the ticket has an
// emailed receipt. Short copy only; every value reaches the page through textContent.

import {
  getTicketReceipts,
  resendReceipt,
  type ApiErrorBody,
  type ReceiptDeliveryDto,
} from "./api.ts";

export interface TicketReceiptDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds money.signature.view (the read). */
  canView: () => boolean;
  /** The session holds money.receipt.resend (the Resend). */
  canResend: () => boolean;
  /** True while the ticket this row serves is still the open one. */
  isCurrent: (orderId: string) => boolean;
}

export interface TicketReceiptRow {
  /** Draws the row for a ticket, or hides it. Reads the server once per call. */
  show(orderId: string | undefined, orderStatus: string | undefined): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

const STATUS_WORDS: Record<string, string> = {
  pending_delivery: "Not sent yet",
  sending: "Sending",
  sent: "Sent",
  failed: "Not sent",
};

function messageOf(body: Partial<ApiErrorBody> | undefined): string {
  return typeof body?.message === "string" && body.message.length > 0
    ? body.message
    : "Something went wrong. Try again.";
}

export function mountTicketReceiptRow(root: HTMLElement, deps: TicketReceiptDeps): TicketReceiptRow {
  let loadSequence = 0;
  let currentOrderId: string | undefined;
  let receipts: ReceiptDeliveryDto[] = [];
  let editing: string | undefined;
  let errorLine = "";
  let busy = false;
  // One key per (receipt, address) until it succeeds, so a double tap or a lost answer cannot send twice.
  const keys = new Map<string, string>();
  root.hidden = true;

  function reset(): void {
    loadSequence += 1;
    currentOrderId = undefined;
    receipts = [];
    editing = undefined;
    errorLine = "";
    busy = false;
    root.replaceChildren();
    root.hidden = true;
  }

  function render(): void {
    root.replaceChildren();
    const emailed = receipts.filter((receipt) => receipt.delivery_method === "emailed");
    if (emailed.length === 0 || currentOrderId === undefined) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    for (const receipt of emailed) {
      const row = node("div", "rcpt-row");
      row.dataset["testid"] = "ticket-receipt-row-item";
      const text = node("div", "rcpt-tx");
      const title = node("b", null, `${receipt.refund_id == null ? "Receipt" : "Refund receipt"} ${(STATUS_WORDS[receipt.delivery_status] ?? "Not sent").toLowerCase()}`);
      title.dataset["testid"] = "ticket-receipt-status";
      title.dataset["status"] = receipt.delivery_status;
      const address = receipt.email?.lastAddress ?? receipt.recipient_email ?? "";
      const sub = node("span", null, address);
      sub.dataset["testid"] = "ticket-receipt-address";
      text.append(title, sub);
      row.append(text);
      if (deps.canResend()) {
        const resend = node("button", "rcpt-btn", "Resend");
        resend.type = "button";
        resend.dataset["testid"] = "ticket-receipt-resend";
        resend.disabled = busy;
        resend.addEventListener("click", () => {
          editing = editing === receipt.id ? undefined : receipt.id;
          errorLine = "";
          render();
        });
        row.append(resend);
      }
      root.append(row);
      if (editing === receipt.id && deps.canResend()) {
        const editor = node("div", "rcpt-edit");
        const input = node("input", "rcpt-input");
        input.type = "email";
        input.inputMode = "email";
        input.autocomplete = "off";
        input.value = address;
        input.setAttribute("aria-label", "Email address");
        input.dataset["testid"] = "ticket-receipt-email";
        const send = node("button", "rcpt-btn rcpt-send", "Send");
        send.type = "button";
        send.dataset["testid"] = "ticket-receipt-send";
        send.disabled = busy;
        send.addEventListener("click", () => void submit(receipt.id, input.value.trim()));
        editor.append(input, send);
        root.append(editor);
      }
    }
    if (errorLine !== "") {
      const error = node("p", "admin-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "ticket-receipt-error";
      root.append(error);
    }
  }

  async function submit(receiptId: string, address: string): Promise<void> {
    const orderId = currentOrderId;
    if (orderId === undefined || busy) return;
    if (address.length === 0) {
      errorLine = "Enter an email address.";
      render();
      return;
    }
    const keyName = `${receiptId}|${address}`;
    const key = keys.get(keyName) ?? crypto.randomUUID();
    keys.set(keyName, key);
    busy = true;
    errorLine = "";
    render();
    const result = await resendReceipt(deps.apiBaseUrl(), deps.tenantId(), orderId, receiptId, {
      idempotencyKey: key,
      recipientEmail: address,
    });
    if (!deps.isCurrent(orderId) || currentOrderId !== orderId) return;
    busy = false;
    if (result.status !== 200 && result.status !== 201) {
      errorLine = messageOf(result.body);
      render();
      return;
    }
    keys.delete(keyName);
    const updated = result.body.receipt;
    if (updated !== null && updated !== undefined) {
      receipts = receipts.map((receipt) => (receipt.id === updated.id ? updated : receipt));
    }
    editing = undefined;
    if (result.body.attempt.outcome === "failed") errorLine = "Not sent. Try again.";
    render();
  }

  async function show(orderId: string | undefined, orderStatus: string | undefined): Promise<void> {
    if (orderId === undefined || orderStatus !== "closed" || !deps.canView()) {
      reset();
      return;
    }
    if (currentOrderId !== orderId) {
      editing = undefined;
      errorLine = "";
    }
    currentOrderId = orderId;
    const sequence = (loadSequence += 1);
    const result = await getTicketReceipts(deps.apiBaseUrl(), deps.tenantId(), orderId);
    if (sequence !== loadSequence || !deps.isCurrent(orderId)) return;
    if (result.status !== 200 || !Array.isArray(result.body.receipts)) {
      receipts = [];
      render();
      return;
    }
    receipts = result.body.receipts;
    render();
  }

  return { show, reset };
}
